import { create } from "zustand";
import { persist } from "zustand/middleware";

import { DEFAULT_GUEST_TOKEN_BALANCE as GUEST_TOKEN_BALANCE } from "../config/env-token-config";
import { axios } from "../libs/axios";
import {
  centralizedTokenService,
  SessionLimitError,
  tokenEventEmitter,
  TOKEN_BALANCE_EVENT,
} from "../services/token/centralized-token.service";

export type TokenStore = {
  // State
  tokenBalance: number | null; // null indicates not yet fetched
  isLoading: boolean;
  lastFetch: number;
  hasFetched: boolean; // Track if we've ever fetched real data
  fetchPromise: Promise<void> | null; // Track in-flight fetch for deduplication
  lastFetchParams: { isLoggedIn: boolean; forceRefresh: boolean } | null; // Track fetch parameters

  // Actions
  setTokenBalance: (balance: number) => void;
  decrementTokens: (amount: number) => void;
  fetchTokenBalance: (isLoggedIn: boolean, forceRefresh?: boolean) => Promise<void>;
  refreshTokenBalance: (isLoggedIn: boolean) => Promise<void>;
  reset: () => void;
};

export const useTokenStore = create<TokenStore>()(
  persist(
    (set, get) => ({
      // Initial state - null means "not yet fetched from server"
      tokenBalance: null,
      isLoading: true, // Start as loading
      lastFetch: 0,
      hasFetched: false,
      fetchPromise: null, // Track in-flight fetch for deduplication
      lastFetchParams: null, // Track parameters to prevent wrong reuse

      // Set token balance
      setTokenBalance: (balance: number) => {
        set({ tokenBalance: balance, hasFetched: true });
        // Dispatch via centralized emitter for global sync
        tokenEventEmitter.dispatch(balance);
      },

      // Decrement tokens (for UI display only - server enforces actual limits)
      decrementTokens: (amount: number) => {
        const currentBalance = get().tokenBalance ?? 0;
        const newBalance = Math.max(0, currentBalance - amount);
        set({ tokenBalance: newBalance });
        tokenEventEmitter.dispatch(newBalance);
      },

      // Fetch token balance using centralized service with deduplication
      fetchTokenBalance: async (isLoggedIn: boolean, forceRefresh = false) => {
        const { lastFetch, hasFetched, fetchPromise, lastFetchParams } = get();
        const now = Date.now();

        // Cache for 30 seconds, but always re-fetch if:
        // - balance is null (not yet fetched)
        // - forceRefresh is requested
        // - auth state changed (guest → logged-in or vice versa): prevents showing
        //   stale guest balance (0) to authenticated users after Zustand persist rehydration
        const currentBalance = get().tokenBalance;
        const authStateChanged = lastFetchParams !== null && lastFetchParams.isLoggedIn !== isLoggedIn;
        if (hasFetched && !forceRefresh && !authStateChanged && currentBalance !== null && now - lastFetch < 30_000) {
          return;
        }

        // If there's already a fetch in progress, check if parameters match
        // CRITICAL: Don't reuse a guest fetch for a logged-in user (or vice versa)
        if (fetchPromise && lastFetchParams) {
          const paramsMatch = 
            lastFetchParams.isLoggedIn === isLoggedIn && 
            lastFetchParams.forceRefresh === forceRefresh;
          
          if (paramsMatch) {
            return fetchPromise;
          } else {
            // Wait for current fetch to complete, then start new one
            await fetchPromise.catch(() => {}); // Ignore errors from previous fetch
          }
        }

        // Create the fetch promise with current parameters
        const currentParams = { isLoggedIn, forceRefresh };
        const promise = (async () => {
          set({ isLoading: true, lastFetch: now, lastFetchParams: currentParams });

          try {
            // Use centralized service for all token fetching
            const balance = await centralizedTokenService.getTokenBalance(isLoggedIn, forceRefresh);
            set({ tokenBalance: balance.balance, hasFetched: true });
            // Note: centralizedTokenService already dispatches the event
          } catch (error) {
            console.error("Failed to fetch token balance:", error);
            // Session limit exceeded — set balance to 0 so pre-generation checks catch it
            if (error instanceof SessionLimitError) {
              set({ tokenBalance: 0, hasFetched: true });
              return;
            }
            // Fallback behavior - only use fallback if we've never successfully fetched
            if (!get().hasFetched) {
              set({ tokenBalance: isLoggedIn ? 0 : GUEST_TOKEN_BALANCE, hasFetched: true });
            }
          } finally {
            set({ isLoading: false, fetchPromise: null, lastFetchParams: null });
          }
        })();

        set({ fetchPromise: promise });
        return promise;
      },

      // Force refresh token balance
      refreshTokenBalance: async (isLoggedIn: boolean) => {
        await get().fetchTokenBalance(isLoggedIn, true);
      },

      // Reset store (useful for logout)
      reset: () => {
        set({
          tokenBalance: null,
          isLoading: true,
          lastFetch: 0,
          hasFetched: false,
          fetchPromise: null,
          lastFetchParams: null,
        });
        tokenEventEmitter.reset();
        centralizedTokenService.clearCache();
      },
    }),
    {
      name: "token-store",
      partialize: (state) => ({
        tokenBalance: state.tokenBalance,
        lastFetch: state.lastFetch,
        hasFetched: state.hasFetched,
      }),
    },
  ),
);

// Token event listener handler (extracted for cleanup capability)
const tokenEventHandler = ((event: CustomEvent<{ balance: number }>) => {
  const currentBalance = useTokenStore.getState().tokenBalance;
  if (event.detail.balance !== currentBalance) {
    useTokenStore.setState({
      tokenBalance: event.detail.balance,
      hasFetched: true,
      isLoading: false,
    });
  }
}) as EventListener;

// Track initialization state to prevent duplicate listeners
let tokenListenerInitialized = false;

/**
 * Initialize the token store event listener
 * Call this in your app's root component (e.g., App.tsx)
 * Returns a cleanup function to remove the listener
 */
export const initTokenStoreListener = (): (() => void) => {
  if (typeof window === "undefined") {
    return () => {}; // No-op for SSR
  }

  if (tokenListenerInitialized) {
    console.warn("[TokenStore] Listener already initialized, skipping");
    return () => {}; // Already initialized
  }

  window.addEventListener(TOKEN_BALANCE_EVENT, tokenEventHandler);
  tokenListenerInitialized = true;

  // Return cleanup function
  return () => {
    window.removeEventListener(TOKEN_BALANCE_EVENT, tokenEventHandler);
    tokenListenerInitialized = false;
  };
};

// Auto-initialize for backward compatibility (but apps should use initTokenStoreListener)
if (typeof window !== "undefined" && !tokenListenerInitialized) {
  initTokenStoreListener();
}

/**
 * @deprecated Use `useCentralizedTokens` from `hooks/use-centralized-tokens.ts` instead.
 * This hook is maintained for backward compatibility only.
 * Hook for consuming AI tokens with automatic deduction
 */
export const useConsumeTokens = () => {
  const { tokenBalance, decrementTokens, fetchTokenBalance } = useTokenStore();

  const consumeTokens = async (
    amount: number,
    isLoggedIn: boolean,
    onSuccess?: () => void,
    onError?: (error: string) => void,
  ) => {
    // Ensure we have the latest token balance
    await fetchTokenBalance(isLoggedIn);

    // Check if user has enough tokens with updated balance
    const currentBalance = useTokenStore.getState().tokenBalance ?? 0;
    if (currentBalance < amount) {
      onError?.(`You've run out of credits. You need ${amount} tokens but only have ${currentBalance}. Upgrade to continue.`);
      return false;
    }

    try {
      if (isLoggedIn) {
        // Deduct from server for authenticated users
        await axios.patch("/user/ai-tokens/track", { tokens: amount });
      }

      // Deduct from local state (for both guest and authenticated users)
      decrementTokens(amount);
      onSuccess?.();
      return true;
    } catch {
      onError?.("Failed to consume tokens. Please try again.");
      return false;
    }
  };

  return { tokenBalance: tokenBalance ?? 0, consumeTokens };
};
